import { useState, useEffect } from 'react';
import { useOne, useGo, useCustomMutation, useApiUrl, useCustom, useList } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { notify } from '@/lib/notify';
import { telegramClientLink } from '@/lib/utils';
import {
  User as UserIcon,
  Loader2,
  ExternalLink,
  Info,
  DollarSign,
  Shield,
  Activity,
  Plus,
  Lock,
  Unlock,
  AlertTriangle,
  Tag,
  ShoppingCart,
  ListChecks,
  Merge,
  Video,
  Bot,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FormHeader } from '@/components/forms';
import { useConfirm } from '@/hooks/use-confirm';
import {
  BLOCK_REASON_LABELS,
  getUserAccountStatus,
  type BlockUserPayload,
  type User,
  type UserBlockHistoryEntry,
} from './types';
import { BlockUserDialog } from './components/BlockUserDialog';
import { MergeUserDialog } from './components/MergeUserDialog';
import { UserLivestreamHistoryPanel } from './components/UserLivestreamHistoryPanel';
import {
  UserFinancials,
  UserAccessTable,
  UserActivityLog,
  UserOverviewAnalytics,
  UserEntitlements,
  SubscriptionCard,
  EntitlementsSummary,
  UserTags,
  UserBots,
} from './components';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface RelatedLead {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  telegramUsername: string | null;
  createdAt: string;
  statusConfig: { id: number; name: string; color: string | null } | null;
}

interface RelatedOrder {
  id: number;
  total: string;
  currency: string;
  createdAt: string;
  statusConfig: { id: number; name: string; color: string | null } | null;
  items?: Array<{ id: number; target?: { name: string } | null; quantity: number }>;
}

function formatShortDate(date: string): string {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getRelatedLeadName(lead: RelatedLead): string {
  const fullName = [lead.firstName, lead.lastName].filter(Boolean).join(' ');
  if (fullName) { return fullName; }
  if (lead.email) { return lead.email; }
  if (lead.telegramUsername) { return `@${lead.telegramUsername}`; }
  if (lead.phone) { return lead.phone; }
  return `Лид #${lead.id}`;
}

function formatRelatedCurrency(value: string, currency: string): string {
  const amount = Number(value);
  const formatted = Number.isFinite(amount)
    ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(amount)
    : value;
  return `${formatted} ${currency}`;
}

/**
 * User show/detail page with tabs
 */
export function UsersShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Поддержка URL-параметра для прямой навигации к табу
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(tabParam || 'overview');
  const [grantAccessModalOpen, setGrantAccessModalOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // Синхронизация с URL-параметром
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const userResult = useOne<User>({
    resource: 'users',
    id: Number(id),
  });

  const user = userResult.result;
  const isLoading = userResult.query.isLoading;

  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();
  const { mutate: blockMutate } = useCustomMutation();
  const { mutate: unblockMutate } = useCustomMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  // История блокировок — один запрос на странице, делим между
  // AccountStatusPanel (нужен автор последнего BLOCK) и BlockHistoryPanel.
  const blockHistoryResult = useCustom<{ data: UserBlockHistoryEntry[] }>({
    url: id ? `${apiUrl}/users/${id}/block-history` : '',
    method: 'get',
    queryOptions: { enabled: Boolean(id) },
  });
  const blockHistoryEntries: UserBlockHistoryEntry[] =
    blockHistoryResult.query.data?.data?.data ?? [];
  // Автор действующей блокировки — ближайшая BLOCK-запись в истории.
  const currentBlockEntry =
    blockHistoryEntries.find((e) => e.action === 'BLOCK') ?? null;

  /**
   * После block/unblock инвалидируем кэш истории блокировок, чтобы
   * BlockHistoryPanel сразу перечитал свежие записи без F5.
   */
  const invalidateBlockHistory = (): void => {
    void queryClient.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey;
        return (
          Array.isArray(key) &&
          key.some(
            (part) =>
              typeof part === 'string' && part.includes(`/users/${user?.id}/block-history`),
          )
        );
      },
    });
  };

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/10 text-green-500">Активна</Badge>;
      case 'expired':
        return <Badge variant="secondary">Истекла</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Отменена</Badge>;
      default:
        return <Badge variant="outline">Нет подписки</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Пользователь не найден</p>
        <Button variant="link" onClick={() => go({ to: '/users' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Без имени';

  const activeSubscription = user.subscriptions[0] ?? null;

  const handleBlockConfirm = async (payload: BlockUserPayload): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
      blockMutate(
        {
          url: `${apiUrl}/users/${user.id}/block`,
          method: 'post',
          values: payload,
        },
        {
          onSuccess: () => {
            notify.success('Пользователь заблокирован');
            void userResult.query.refetch();
            invalidateBlockHistory();
            resolve();
          },
          onError: (error) => {
            reject(error instanceof Error ? error : new Error('Не удалось заблокировать'));
          },
        },
      );
    });
  };

  const handleUnblock = async (): Promise<void> => {
    const confirmed = await confirm({
      title: `Разблокировать пользователя «${fullName}»?`,
      description: 'Пользователь снова сможет пользоваться ботом и платформой.',
      confirmText: 'Разблокировать',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }

    unblockMutate(
      {
        url: `${apiUrl}/users/${user.id}/unblock`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Пользователь разблокирован');
          void userResult.query.refetch();
          invalidateBlockHistory();
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : 'Не удалось разблокировать';
          notify.error(message);
        },
      },
    );
  };

  const formatBlockDate = (iso: string | null | undefined): string => {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={fullName}
        description={user.username ? `@${user.username}` : undefined}
        onBack={() => go({ to: '/users' })}
        titleAddon={getStatusBadge(activeSubscription?.status)}
        actions={
          <div className="flex items-center gap-2">
            {activeTab === 'access' && (
              <Button onClick={() => setGrantAccessModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Дать доступ
              </Button>
            )}
            {telegramClientLink(user) && (
              <Button variant="outline" asChild>
                <a
                  href={telegramClientLink(user) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Открыть в Telegram
                </a>
              </Button>
            )}
            <Button variant="outline" onClick={() => setMergeDialogOpen(true)}>
              <Merge className="mr-2 h-4 w-4" />
              Объединить
            </Button>
          </div>
        }
        icon={<UserIcon className="h-5 w-5" />}
        sticky
      />

      {/* Статус аккаунта */}
      <AccountStatusPanel
        user={user}
        blockedByAdmin={currentBlockEntry}
        onBlock={() => setBlockDialogOpen(true)}
        onUnblock={() => { void handleUnblock(); }}
        formatDate={formatBlockDate}
      />

      {/* История блокировок — не рендерится если пусто */}
      <BlockHistoryPanel
        entries={blockHistoryEntries}
        isLoading={blockHistoryResult.query.isLoading}
        formatDate={formatBlockDate}
      />

      {/* UTM-атрибуция — показываем только если есть хотя бы одна метка */}
      {(user.utmSource ?? user.utmMedium ?? user.utmCampaign) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              UTM-атрибуция
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-2 sm:grid-cols-3 text-sm">
              {user.utmSource && (
                <div>
                  <dt className="text-xs text-muted-foreground">Источник (utm_source)</dt>
                  <dd className="font-medium">{user.utmSource}</dd>
                </div>
              )}
              {user.utmMedium && (
                <div>
                  <dt className="text-xs text-muted-foreground">Канал (utm_medium)</dt>
                  <dd className="font-medium">{user.utmMedium}</dd>
                </div>
              )}
              {user.utmCampaign && (
                <div>
                  <dt className="text-xs text-muted-foreground">Кампания (utm_campaign)</dt>
                  <dd className="font-medium">{user.utmCampaign}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-8 lg:inline-flex lg:w-auto">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            <span className="hidden sm:inline">Обзор</span>
          </TabsTrigger>
          <TabsTrigger value="financials" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">Финансы</span>
          </TabsTrigger>
          <TabsTrigger value="access" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Доступы и подписки</span>
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Теги</span>
          </TabsTrigger>
          <TabsTrigger value="crm" className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            <span className="hidden sm:inline">CRM</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Активность</span>
          </TabsTrigger>
          <TabsTrigger value="livestreams" className="flex items-center gap-2">
            <Video className="h-4 w-4" />
            <span className="hidden sm:inline">Трансляции</span>
          </TabsTrigger>
          <TabsTrigger value="bots" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            <span className="hidden sm:inline">Боты</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" forceMount className="data-[state=inactive]:hidden">
          <UserOverviewAnalytics userId={user.id} />
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" forceMount className="data-[state=inactive]:hidden">
          <UserFinancials
            userId={user.id}
            subscription={
              activeSubscription
                ? {
                    id: activeSubscription.id,
                    status: activeSubscription.status,
                    endDate: activeSubscription.endDate ?? null,
                    planCode: activeSubscription.planCode ?? null,
                  }
                : null
            }
          />
        </TabsContent>

        {/* Access Tab */}
        <TabsContent value="access" forceMount className="space-y-6 data-[state=inactive]:hidden">
          {/* Карточка подписки */}
          <SubscriptionCard subscriptions={user.subscriptions} />

          {/* Summary карточки */}
          <EntitlementsSummary
            coursesCount={user.accessibleCourses?.length ?? 0}
            channelsCount={user.accessibleChannels?.length ?? 0}
            userId={user.id}
          />

          {/* Единая таблица всех entitlements */}
          <UserEntitlements userId={user.id} />

          {/* Модалка выдачи доступа (таблица скрыта — данные в UserEntitlements) */}
          <div className="hidden">
            <UserAccessTable
              userId={user.id}
              grantModalOpen={grantAccessModalOpen}
              onGrantModalOpenChange={setGrantAccessModalOpen}
            />
          </div>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" forceMount className="data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Теги пользователя
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UserTags userId={user.id} tags={user.tags} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="crm" forceMount className="data-[state=inactive]:hidden">
          <UserCrmRelations userId={user.id} />
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" forceMount className="data-[state=inactive]:hidden">
          <UserActivityLog userId={user.id} />
        </TabsContent>

        {/* Livestreams Tab */}
        <TabsContent value="livestreams" forceMount className="data-[state=inactive]:hidden">
          <UserLivestreamHistoryPanel userId={user.id} />
        </TabsContent>

        {/* Bots Tab */}
        <TabsContent value="bots" forceMount className="data-[state=inactive]:hidden">
          <UserBots userId={user.id} />
        </TabsContent>
      </Tabs>

      <BlockUserDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        onConfirm={handleBlockConfirm}
        userName={`«${fullName}»`}
      />

      <MergeUserDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        survivorId={user.id}
        survivorName={fullName}
        onMerged={() => {
          notify.success('Аккаунты объединены');
          void userResult.query.refetch();
          invalidateBlockHistory();
        }}
      />

      {ConfirmationDialog}
    </div>
  );
}

function UserCrmRelations({ userId }: { userId: number }) {
  const go = useGo();
  const leadsResult = useList<RelatedLead>({
    resource: 'leads',
    pagination: { pageSize: 8 },
    filters: [{ field: 'userId', operator: 'eq', value: userId }],
    sorters: [{ field: 'createdAt', order: 'desc' }],
  });
  const ordersResult = useList<RelatedOrder>({
    resource: 'orders',
    pagination: { pageSize: 8 },
    filters: [{ field: 'userId', operator: 'eq', value: userId }],
    sorters: [{ field: 'createdAt', order: 'desc' }],
  });

  const leads = leadsResult.result?.data ?? [];
  const orders = ordersResult.result?.data ?? [];
  const leadsLoading = leadsResult.query?.isLoading ?? false;
  const ordersLoading = ordersResult.query?.isLoading ?? false;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Лиды
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {leadsLoading && (
            <div className="text-sm text-muted-foreground">Загрузка лидов...</div>
          )}
          {!leadsLoading && leads.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              У пользователя пока нет связанных лидов
            </div>
          )}
          {leads.map((lead) => (
            <button
              key={lead.id}
              type="button"
              className="w-full rounded-md border p-3 text-left transition-colors hover:bg-muted/50"
              onClick={() => go({ to: `/leads/show/${lead.id}` })}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{getRelatedLeadName(lead)}</div>
                  <div className="text-xs text-muted-foreground">Лид #{lead.id} · {formatShortDate(lead.createdAt)}</div>
                </div>
                {lead.statusConfig && (
                  <Badge
                    variant="outline"
                    className="shrink-0"
                    style={{
                      borderColor: lead.statusConfig.color ?? undefined,
                      color: lead.statusConfig.color ?? undefined,
                    }}
                  >
                    {lead.statusConfig.name}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Заказы
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {ordersLoading && (
            <div className="text-sm text-muted-foreground">Загрузка заказов...</div>
          )}
          {!ordersLoading && orders.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              У пользователя пока нет заказов
            </div>
          )}
          {orders.map((order) => {
            const firstItem = order.items?.[0] ?? null;
            return (
              <button
                key={order.id}
                type="button"
                className="w-full rounded-md border p-3 text-left transition-colors hover:bg-muted/50"
                onClick={() => go({ to: `/orders/show/${order.id}` })}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">Заказ #{order.id}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatShortDate(order.createdAt)}
                      {firstItem?.target?.name ? ` · ${firstItem.target.name}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-sm">{formatRelatedCurrency(order.total, order.currency)}</div>
                    {order.statusConfig && (
                      <Badge
                        variant="outline"
                        className="mt-1"
                        style={{
                          borderColor: order.statusConfig.color ?? undefined,
                          color: order.statusConfig.color ?? undefined,
                        }}
                      >
                        {order.statusConfig.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

interface AccountStatusPanelProps {
  user: User;
  /**
   * Последнее действие BLOCK из истории — источник информации об админе,
   * который текущую блокировку выставил. Null = историю ещё грузим или
   * юзер не был заблокирован.
   */
  blockedByAdmin: UserBlockHistoryEntry | null;
  onBlock: () => void;
  onUnblock: () => void;
  formatDate: (iso: string | null | undefined) => string;
}

/**
 * Отрисовка человекочитаемого имени админа с fallback-цепочкой:
 * firstName lastName → email → "Админ #<id>" → "Система".
 */
function formatAdminLabel(
  entry: Pick<UserBlockHistoryEntry, 'performedBy' | 'performedByName' | 'performedByEmail'> | null,
): string {
  if (!entry) { return 'Неизвестный админ'; }
  if (entry.performedByName) { return entry.performedByName; }
  if (entry.performedByEmail) { return entry.performedByEmail; }
  if (entry.performedBy !== null) { return `Админ #${entry.performedBy}`; }
  return 'Система';
}

/**
 * Панель статуса аккаунта: активен / заблокирован / удалён.
 * Для заблокированного показывает причину, кого и когда, и кнопку разблокировки.
 */
function AccountStatusPanel({
  user,
  blockedByAdmin,
  onBlock,
  onUnblock,
  formatDate,
}: AccountStatusPanelProps): React.ReactElement {
  const status = getUserAccountStatus(user);

  if (status === 'deleted') {
    return (
      <div className="rounded-lg border border-muted bg-muted/20 p-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline">Удалён</Badge>
          <span className="text-sm text-muted-foreground">
            Учётная запись удалена (soft-delete). Административные действия недоступны.
          </span>
        </div>
      </div>
    );
  }

  if (status === 'blocked') {
    const reasonLabel = user.blockReasonCode
      ? BLOCK_REASON_LABELS[user.blockReasonCode]
      : 'Не указан';
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="destructive">Заблокирован</Badge>
                <span className="text-sm font-medium">{reasonLabel}</span>
              </div>
              {user.blockReason && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {user.blockReason}
                </p>
              )}
              <div className="text-xs text-muted-foreground space-y-1">
                {user.blockedAt && (
                  <div>
                    Заблокирован: <span className="font-medium">{formatDate(user.blockedAt)}</span>
                  </div>
                )}
                {(blockedByAdmin || (user.blockedBy !== null && user.blockedBy !== undefined)) && (
                  <div>
                    Админ: <span className="font-medium">{formatAdminLabel(
                      blockedByAdmin ?? { performedBy: user.blockedBy ?? null, performedByName: null, performedByEmail: null },
                    )}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onUnblock}>
            <Unlock className="mr-2 h-4 w-4" />
            Разблокировать
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
            Активен
          </Badge>
          <span className="text-sm text-muted-foreground">Учётная запись работает штатно.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onBlock}>
          <Lock className="mr-2 h-4 w-4" />
          Заблокировать
        </Button>
      </div>
    </div>
  );
}

interface BlockHistoryPanelProps {
  entries: UserBlockHistoryEntry[];
  isLoading: boolean;
  formatDate: (iso: string | null | undefined) => string;
}

/**
 * Полная история блокировок/разблокировок юзера.
 * Данные приходят из useCustom в родителе (один запрос на страницу),
 * панель только рендерит. Не рендерится если история пустая.
 */
function BlockHistoryPanel({
  entries,
  isLoading,
  formatDate,
}: BlockHistoryPanelProps): React.ReactElement | null {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка истории блокировок…
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">История блокировок</h3>
        <Badge variant="outline" className="ml-auto">
          {entries.length}
        </Badge>
      </div>

      <ol className="space-y-2">
        {entries.map((entry) => {
          const isBlock = entry.action === 'BLOCK';
          const reasonLabel = entry.reasonCode
            ? BLOCK_REASON_LABELS[entry.reasonCode]
            : null;

          return (
            <li
              key={entry.id}
              className="flex items-start gap-3 rounded-md border p-3 text-sm"
            >
              <div className="mt-0.5 flex-shrink-0">
                {isBlock ? (
                  <Lock className="h-4 w-4 text-destructive" />
                ) : (
                  <Unlock className="h-4 w-4 text-green-600" />
                )}
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={isBlock ? 'destructive' : 'outline'}>
                    {isBlock ? 'Заблокирован' : 'Разблокирован'}
                  </Badge>
                  {reasonLabel && (
                    <span className="font-medium">{reasonLabel}</span>
                  )}
                </div>
                {entry.reason && (
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {entry.reason}
                  </p>
                )}
                <div className="text-xs text-muted-foreground">
                  {formatAdminLabel(entry)} • {formatDate(entry.performedAt)}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
